import { defaultDropAnimation, DropAnimation } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { FlattenedItem, TreeNode } from '@/common/components/Tree/TreeItemView/TreeItemView.types';
import { getParentId } from '@/common/helpers/tree.utils';

export function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / indentationWidth);
}

export function getProjection<T extends TreeNode<T>>(
  items: FlattenedItem<T>[],
  activeId: StringOrNumber,
  overId: StringOrNumber,
  dragOffset: number,
  indentationWidth: number,
) {
  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);

  const activeItem = items[activeItemIndex];

  const newItems = arrayMove(items, activeItemIndex, overItemIndex);

  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];

  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;

  const maxDepth = getMaxDepth({ previousItem });
  const minDepth = getMinDepth({ nextItem });

  let depth = projectedDepth;

  if (projectedDepth >= maxDepth) {
    depth = maxDepth;
  } else if (projectedDepth < minDepth) {
    depth = minDepth;
  }

  return { depth, maxDepth, minDepth, parentId: getParentId(depth, previousItem, newItems, overItemIndex) };
}

export function getMaxDepth<T extends TreeNode<T>>({ previousItem }: { previousItem: FlattenedItem<T> }) {
  if (previousItem) {
    return previousItem.depth + 1;
  }

  return 0;
}

export function getMinDepth<T extends TreeNode<T>>({ nextItem }: { nextItem: FlattenedItem<T> }) {
  return nextItem?.depth ?? 0;
}

export function buildTree<T extends TreeNode<T>>(flattenedItems: FlattenedItem<T>[]): T[] {
  const root = { id: 'root', children: [] } as unknown as T;
  const nodes: Record<string, T> = { [root.id]: root };

  const items: FlattenedItem<T>[] = flattenedItems.map((item) => ({
    ...item,
    original: { ...item.original, children: [] },
  }));

  const driveItems = items.map((item) => item.original);

  for (const item of items) {
    const { id, children = [], ...rest } = item.original;
    const parentId = item.parentId ?? root.id;

    const parent = nodes[parentId] ?? driveItems.find(({ id }) => id === parentId);

    nodes[id] = { id, children, ...rest } as T;

    parent.children!.push(item.original);
  }

  return root.children!;
}

// If an item matches the predicate, return it with all its children;
// otherwise, filter its children recursively.
export function filterTreeDeep<T extends TreeNode<T>>(items: T[], predicate: (item: T) => boolean): T[] {
  return items.flatMap((item) => {
    return predicate(item) ? item : filterTreeDeep(item?.children ?? [], predicate);
  });
}

export const treeDropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};
